/**
 * Prepares the voice transport during a user-started introduction, then
 * activates microphone capture and the opening greeting after the clip ends.
 * A failed/expired preparation is retried once on activation; it never
 * interrupts the introduction. No voice events are recorded before activation.
 */

import { createPublicApiClient, type PublicApiClient } from '@cloneai/api-client';
import { logger, metrics } from '@cloneai/observability';
import type { AgentPublicConfig } from '@cloneai/shared-types';
import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState, type Dispatch } from 'react';

import { env } from '../env.js';
import type { AgentEvent } from '../state.js';
import { OpenAIRealtimeSession } from '../voice/openaiRealtimeSession.js';
import { SessionRecorder } from '../voice/sessionRecorder.js';
import type { VoiceSession } from '../voice/VoiceSession.js';

// Long or paused introductions must not hold an idle provider connection
// indefinitely. Expired preparations are recreated only when voice is needed.
export const VOICE_PREPARATION_IDLE_MS = 120_000;

let apiClientSingleton: PublicApiClient | null = null;
function getApiClient(): PublicApiClient {
  if (!apiClientSingleton) {
    apiClientSingleton = createPublicApiClient({ baseUrl: env.VITE_API_BASE_URL });
  }
  return apiClientSingleton;
}

export function useVoiceSession(
  config: AgentPublicConfig | null,
  tenantSlug: string | null,
  agentSlug: string | null,
  lang: string | null,
  isActive: boolean,
  dispatch: Dispatch<AgentEvent>,
  prepareDuringIntro = false,
): VoiceSession | null {
  const [session, setSession] = useState<VoiceSession | null>(null);
  const activeSessionRef = useRef<VoiceSession | null>(null);
  const lifecycleRef = useRef<{
    activate: () => void;
    changeLanguage: (language: string) => void;
  } | null>(null);
  const latestConfigRef = useRef(config);
  const latestLangRef = useRef(lang);
  const shouldPrepare =
    prepareDuringIntro &&
    config?.interactionMode === 'voice' &&
    config.voiceProvider === 'openai-realtime';
  const enabled = isActive || shouldPrepare;

  useEffect(() => {
    latestConfigRef.current = config;
    latestLangRef.current = lang;
    if (lang) lifecycleRef.current?.changeLanguage(lang);
  }, [config, lang]);

  useEffect(() => {
    if (!enabled || !tenantSlug || !agentSlug) return undefined;
    const sessionConfig = latestConfigRef.current;
    const activeLang = latestLangRef.current;
    if (!sessionConfig || !activeLang) return undefined;
    const requestedLang: string = activeLang;

    let cancelled = false;
    let activated = false;
    let starting = false;
    let started = false;
    let failed = false;
    let activationStartedAt: number | null = null;
    let prepared: VoiceSession | null = null;
    let preparationError: unknown = null;
    let preparationGeneration = 0;
    let preparationLanguage = requestedLang;
    let preparationFailureRecorded = false;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    // Snapshot the initial mode: an unsuccessful speculative attempt may be
    // retried at clip end; an ordinary startup failure keeps the existing retry UI.
    const beganDuringIntro = shouldPrepare;
    const client = getApiClient();
    const metricTags = {
      tenantSlug,
      agentSlug,
      lang: requestedLang,
      voiceProvider: sessionConfig.voiceProvider,
    };
    const existing = (window as unknown as { __cloneai_recorder?: SessionRecorder })
      .__cloneai_recorder;
    const recorder =
      existing ??
      new SessionRecorder({
        apiBaseUrl: env.VITE_API_BASE_URL,
        tenantSlug,
        productSlug: agentSlug,
        productType: 'agent',
        origin: window.location.origin,
      });

    function clearIdleTimer(): void {
      if (idleTimer !== null) clearTimeout(idleTimer);
      idleTimer = null;
    }

    function stopPrepared(): void {
      clearIdleTimer();
      const current = prepared;
      prepared = null;
      if (activeSessionRef.current === current) activeSessionRef.current = null;
      if (!current) return;
      delete current.onSessionEnded;
      delete current.onError;
      current.stop();
    }

    function fail(error: unknown): void {
      if (cancelled || failed) return;
      failed = true;
      stopPrepared();
      const message = error instanceof Error ? error.message : 'Unknown voice session error';
      logger.error(
        started ? 'agent voice session runtime error' : 'agent voice session start failed',
        { ...metricTags, message },
      );
      metrics.increment(
        started ? 'agent.voice_session.error' : 'agent.voice_session.start_failed',
        1,
        { tags: metricTags },
      );
      if (!started && activationStartedAt !== null) {
        metrics.timing(
          'agent.voice_session.start_failed.duration',
          performance.now() - activationStartedAt,
          {
            tags: metricTags,
          },
        );
      }
      recorder.recordImmediateEvent({
        type: 'error',
        timestamp: new Date().toISOString(),
        payload: { code: started ? 'voice-session-error' : 'voice-session-start-failed', message },
      });
      Sentry.captureException(error);
      dispatch({ type: 'SESSION_FAILED' });
    }

    function recordPreparationFailure(): void {
      if (preparationFailureRecorded) return;
      preparationFailureRecorded = true;
      metrics.increment('agent.voice_session.prepare_failed', 1, { tags: metricTags });
    }

    async function prepare(): Promise<VoiceSession | null> {
      const generation = ++preparationGeneration;
      preparationFailureRecorded = false;
      preparationLanguage = latestLangRef.current ?? requestedLang;
      const startedAt = performance.now();
      const preparationTags = {
        ...metricTags,
        lang: preparationLanguage,
        phase: beganDuringIntro && !activated ? 'intro' : 'activation',
      };
      try {
        const response = await client.publicAgents.mintSession(
          tenantSlug as string,
          agentSlug as string,
          {
            lang: preparationLanguage,
            origin: window.location.origin,
          },
        );
        if (cancelled || generation !== preparationGeneration) return null;
        metrics.timing('agent.voice_session.mint.duration', performance.now() - startedAt, {
          tags: { ...preparationTags, kind: response.kind },
        });
        if (response.kind !== 'openai-realtime') {
          throw new Error('Vapi adapter not yet implemented (P2-09)');
        }
        const created = new OpenAIRealtimeSession(response, recorder, preparationLanguage, {
          knowledgeTool: {
            search: async (query: string) => {
              const sessionId = await recorder.getSessionId();
              if (!sessionId) return { results: [] };
              return client.publicSessions.searchKnowledge(sessionId, {
                query,
                lang: latestLangRef.current ?? requestedLang,
              });
            },
          },
          onFirstAudio: () => {
            if (cancelled || failed || activationStartedAt === null) return;
            metrics.timing(
              'agent.voice_session.first_audio.duration',
              performance.now() - activationStartedAt,
              {
                tags: metricTags,
              },
            );
          },
        });
        // Take ownership before awaiting preparation so cancellation can close
        // the socket/worklet even while the provider handshake is in flight.
        prepared = created;
        activeSessionRef.current = created;
        created.onError = (error: Error): void => {
          preparationError = error;
          if (starting) fail(error);
          else {
            recordPreparationFailure();
            stopPrepared();
          }
        };
        created.onSessionEnded = (): void => {
          preparationError = new Error('Voice connection ended');
          if (!starting) {
            recordPreparationFailure();
            stopPrepared();
          } else if (!started) fail(preparationError);
          else dispatch({ type: 'SESSION_ENDED' });
        };
        await created.prepare();
        if (cancelled || generation !== preparationGeneration || prepared !== created) return null;
        metrics.timing('agent.voice_session.prepare.duration', performance.now() - startedAt, {
          tags: preparationTags,
        });
        if (!activated) {
          idleTimer = setTimeout(() => {
            preparationError = new Error('Voice preparation expired');
            stopPrepared();
          }, VOICE_PREPARATION_IDLE_MS);
        }
        return created;
      } catch (error) {
        if (cancelled || generation !== preparationGeneration) return null;
        preparationError = error;
        stopPrepared();
        if (beganDuringIntro && !starting) recordPreparationFailure();
        return null;
      }
    }

    let preparation = prepare();
    async function activate(): Promise<void> {
      if (activated || cancelled) return;
      activated = true;
      clearIdleTimer();
      activationStartedAt = performance.now();
      recorder.recordEvent({ type: 'voice-start', timestamp: new Date().toISOString() });
      void recorder.start();
      metrics.increment('agent.voice_session.start_requested', 1, { tags: metricTags });
      Sentry.addBreadcrumb({
        category: 'agent.voice',
        message: 'voice activation started',
        level: 'info',
        data: metricTags,
      });
      try {
        let created = await preparation;
        if (cancelled || failed) return;
        if (created !== prepared) created = null;
        if (!created && beganDuringIntro) created = await prepare();
        if (cancelled || failed) return;
        if (!created) throw preparationError ?? new Error('Voice preparation failed');
        // Publish the adapter before starting audio so AgentStage can attach
        // speech/transcript callbacks to the prepared session.
        setSession(created);
        starting = true;
        await created.start();
        if (cancelled || failed) return;
        started = true;
        metrics.timing(
          'agent.voice_session.ready.duration',
          performance.now() - activationStartedAt,
          { tags: metricTags },
        );
        metrics.increment('agent.voice_session.started', 1, { tags: metricTags });
        logger.info('agent voice session ready', metricTags);
        dispatch({ type: 'SESSION_MINT_DONE' });
      } catch (error) {
        fail(error);
      }
    }
    lifecycleRef.current = {
      activate: () => {
        void activate();
      },
      changeLanguage: (language) => {
        if (activated) {
          activeSessionRef.current?.setLanguage?.(language);
          return;
        }
        if (language === preparationLanguage) return;
        // A descriptor contains localized instructions and knowledge, not just
        // a language hint. Remint it when the visitor changes intro language.
        metricTags.lang = language;
        stopPrepared();
        preparation = prepare();
      },
    };

    return (): void => {
      cancelled = true;
      lifecycleRef.current = null;
      stopPrepared();
      setSession(null);
      if (activated)
        recorder.recordEvent({ type: 'voice-end', timestamp: new Date().toISOString() });
      // App owns the recorder's public session and its final /end request.
    };
    // A single lifecycle deliberately spans intro → connecting → voice-active.
    // Language/config are read from refs to avoid reminting during a live call.
  }, [enabled, tenantSlug, agentSlug, dispatch]);

  useEffect(() => {
    if (isActive) lifecycleRef.current?.activate();
  }, [isActive, enabled, tenantSlug, agentSlug]);

  return session;
}
